export default class DateFns {
	localeMapping: Record<string, string> = {
		zh_TW: 'zh-TW',
		zh_MO: 'zh-TW',
		zh_HK: 'zh-TW',
		zh_CN: 'zh-CN',
		ja: 'ja',
		en_US: 'en-US',
		en_SG: 'en-US',
		en_PH: 'en-US',
		en_MY: 'en-US',
		en_IN: 'en-IN',
		en_AU: 'en-AU',
		en_AE: 'en-US',
		en_GB: 'en-GB',
		vi: 'vi',
	}

	getLocale(lang: string) {
		return this.localeMapping[lang] || 'en-US'
	}

	translateOffset2TimeZone(offset: string) {
		const isNegative = offset[0] === '-'
		const hoursMatch = offset.match(/\d+/)
		const minutes = offset.match(/\.\d+$/)
		const hours = hoursMatch === null ? '0' : hoursMatch[0]
		const hoursStr = hours.length === 1 ? `0${hours}` : hours
		const minutesStr =
			minutes === null
				? '00'
				: (parseInt(minutes[0].replace('.', ''), 10) * 6).toString()
		return (isNegative ? '-' : '+') + hoursStr + minutesStr
	}

	formatDate(
		date: string | number | Date,
		pattern: string,
		lang: string,
		timeZone?: string | null,
	) {
		const locale = this.getLocale(lang)
		let convertDate: Date

		if (typeof date === 'number') {
			convertDate = date < 10000000000 ? new Date(date * 1000) : new Date(date)
		} else if (typeof date === 'string') {
			convertDate = new Date(date)
		} else {
			convertDate = date
		}

		if (Number.isNaN(convertDate.getTime())) {
			return '-'
		}

		const options = this.parsePatternToOptions(pattern)

		if (timeZone) {
			const timeZoneFormat = this.translateOffset2TimeZone(timeZone)
			options.timeZone = timeZoneFormat
		}

		try {
			const formatter = new Intl.DateTimeFormat(locale, options)
			let result = formatter.format(convertDate)

			if (pattern.includes('-')) {
				result = result.replace(/\//g, '-')
			} else if (pattern.includes('/')) {
				result = result.replace(/-/g, '/')
			}

			return result
		} catch (error) {
			console.warn('Date formatting error:', error)
			return convertDate.toLocaleDateString(locale)
		}
	}

	private parsePatternToOptions(pattern: string): Intl.DateTimeFormatOptions {
		const patternMap: Record<string, Intl.DateTimeFormatOptions> = {
			'yyyy-MM-dd': { year: 'numeric', month: '2-digit', day: '2-digit' },
			'yyyy-MM-dd HH:mm:ss': {
				year: 'numeric',
				month: '2-digit',
				day: '2-digit',
				hour: '2-digit',
				minute: '2-digit',
				second: '2-digit',
				hour12: false,
			},
			'HH:mm:ss dd-MM-yyyy': {
				hour: '2-digit',
				minute: '2-digit',
				second: '2-digit',
				day: '2-digit',
				month: '2-digit',
				year: 'numeric',
				hour12: false,
			},
		}

		if (patternMap[pattern]) {
			return patternMap[pattern]
		}

		const options: Intl.DateTimeFormatOptions = {}

		if (pattern.includes('yyyy') || pattern.includes('YYYY')) {
			options.year = 'numeric'
		}
		if (pattern.includes('MM')) {
			options.month = '2-digit'
		} else if (pattern.includes('M')) {
			options.month = 'numeric'
		}
		if (pattern.includes('dd') || pattern.includes('DD')) {
			options.day = '2-digit'
		} else if (pattern.includes('d') || pattern.includes('D')) {
			options.day = 'numeric'
		}
		if (pattern.includes('HH') || pattern.includes('hh')) {
			options.hour = '2-digit'
			options.hour12 = false
		} else if (pattern.includes('H') || pattern.includes('h')) {
			options.hour = 'numeric'
			options.hour12 = false
		}
		if (pattern.includes('mm')) {
			options.minute = '2-digit'
		} else if (pattern.includes('m')) {
			options.minute = 'numeric'
		}
		if (pattern.includes('ss')) {
			options.second = '2-digit'
		} else if (pattern.includes('s')) {
			options.second = 'numeric'
		}

		if (Object.keys(options).length === 0) {
			return {
				year: 'numeric',
				month: '2-digit',
				day: '2-digit',
				hour: '2-digit',
				minute: '2-digit',
				hour12: false,
			}
		}

		return options
	}
}

export function toDateTime(
	timestamp: number | string | Date | null | undefined,
	timezone?: string | undefined | null,
	timeFormat = 'yyyy-MM-dd HH:mm:ss',
) {
	if (timestamp !== null && timestamp !== undefined) {
		const timestampConverted =
			typeof timestamp === 'number' ? timestamp * 1000 : timestamp

		return new DateFns().formatDate(
			timestampConverted,
			`${timeFormat}`,
			window.BUYANDSHIP.settings.current_language,
			timezone,
		)
	}

	return '-'
}
